import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  type AdNamingConvention,
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateDefaultsMutation,
} from "~/ui-rtk/api/adForgeApi"
import { TagCategoryRow } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel/TagCategoryRow"
import { Button } from "~/ui-rtk/components/ui/controls"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import Input from "~/ui-rtk/components/ui/controls/Input"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import AdNameTester from "./AdNameTester"
import { PoolChip, TemplateChip } from "./NamingChips"

/**
 * Company-level ad-naming convention: click tag categories into the order they
 * appear in an ad name.
 *
 * Storing positions (rather than a hand-typed `{gender}_{talent}` string) is
 * what makes names machine-readable in both directions:
 *
 *   compose → AdForge's "Build ad name from tags" fills each position
 *   parse   → an existing ad name splits on the separator, token N → position N
 *
 * The `{slug}` template AdForge/AdBlaster actually read is derived server-side
 * from what this saves, so the composer, the required-tags rule, and the parser
 * can never disagree about the convention.
 *
 * Categories are created and renamed here too — the convention is useless if
 * you have to go to another page to add the category you're missing mid-thought.
 */

/**
 * Quick-fill buttons for the separator; the field itself takes any string of 1-8
 * characters. Empty is rejected (the API enforces it too): with no delimiter the
 * values run together and there's nothing to split a name on when parsing.
 */
const SEPARATOR_PRESETS = ["_", "-", "|", " | ", "/", " "]

/**
 * Stable identities for the taxonomy rows' inert selection props — new literals
 * would give every `TagCategoryRow` a fresh prop on each render.
 */
const NO_SELECTION: string[] = []
const noop = () => undefined

/** A row in the template. `uid` keeps React keys stable across reordering. */
interface PositionRow {
  uid: string
  slug: string
}

let rowSeq = 0
function makeRow(slug: string): PositionRow {
  rowSeq += 1
  return { uid: `row-${rowSeq}`, slug }
}

/** Human-readable rendering of a separator, so " " isn't invisible in the UI. */
function describeSeparator(separator: string): string {
  if (separator === " ") return "space"
  if (separator === "") return "none"
  if (separator.trim() === "") return `${separator.length} spaces`
  return `"${separator}"`
}

export default function AdNamingSettings() {
  const { data: defaults, isLoading: isLoadingDefaults } =
    useAdForgeGetDefaultsQuery()
  const {
    categories: allCategories,
    isLoading: isLoadingCategories,
    createCategory,
    createOption,
    renameCategory,
  } = useTagCategories()
  const [updateDefaults, { isLoading: isSaving }] =
    useAdForgeUpdateDefaultsMutation()

  const categories = useMemo(
    () => [...allCategories].sort((a, b) => a.sortOrder - b.sortOrder),
    [allCategories],
  )

  const [separator, setSeparator] = useState("_")
  const [positions, setPositions] = useState<PositionRow[]>([])
  // One-shot hydration, so a cache refresh (another tab saving the defaults)
  // can't wipe an in-progress edit.
  const [hydrated, setHydrated] = useState(false)

  const [newCategoryLabel, setNewCategoryLabel] = useState("")
  const [newCategoryIsFreeText, setNewCategoryIsFreeText] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (hydrated || !defaults) return
    setHydrated(true)
    const naming = defaults.adNaming
    if (!naming) return
    setSeparator(naming.separator ?? "_")
    setPositions(naming.positions.map(p => makeRow(p.slug)))
  }, [defaults, hydrated])

  const categoryBySlug = useMemo(
    () => new Map(categories.map(c => [c.slug, c])),
    [categories],
  )
  const labelFor = (slug: string) =>
    formatTagLabel(categoryBySlug.get(slug)?.label ?? slug)

  const usedSlugs = useMemo(
    () => new Set(positions.map(row => row.slug)),
    [positions],
  )
  const poolCategories = categories.filter(c => !usedSlugs.has(c.slug))

  /** Append a category as the next position (no-op if already placed). */
  const addPosition = (slug: string) =>
    setPositions(prev =>
      prev.some(row => row.slug === slug) ? prev : [...prev, makeRow(slug)],
    )

  const move = (index: number, delta: number) =>
    setPositions(prev => {
      const target = index + delta
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const slugs = positions.map(row => row.slug)

  // Example name from each category's first real option (falling back to the
  // category label when it has no values yet).
  const preview = useMemo(() => {
    if (slugs.length === 0) return ""
    return slugs
      .map(slug => {
        const category = categoryBySlug.get(slug)
        return formatTagLabel(
          category?.options[0]?.label ?? category?.label ?? slug,
        )
      })
      .join(separator)
  }, [slugs, categoryBySlug, separator])

  const handleCreateCategory = async () => {
    const label = newCategoryLabel.trim()
    if (!label) return
    setIsCreating(true)
    try {
      // `mixed` + allowMultiple:false is the shape a naming position wants: one
      // value per ad, and editors can add new values as they go (a new talent
      // name shouldn't need a settings trip). `free_text` categories have no
      // value list at all — the value is typed per ad (separator characters
      // are stripped so they can't corrupt name parsing).
      const created = await createCategory({
        label,
        valueType: newCategoryIsFreeText ? "free_text" : "mixed",
        allowMultiple: false,
      })
      setNewCategoryLabel("")
      setNewCategoryIsFreeText(false)
      // Straight into the template — creating it here means you want it.
      addPosition(created.slug)
      toast.success(`Added "${created.label}"`)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to create the category",
      )
    } finally {
      setIsCreating(false)
    }
  }

  const handleSave = async () => {
    const convention: AdNamingConvention = {
      separator,
      positions: slugs.map(slug => ({ slug })),
    }
    try {
      const saved = await updateDefaults({ adNaming: convention }).unwrap()
      toast.success(
        slugs.length === 0
          ? "Ad naming convention cleared"
          : `Ad naming saved — ${saved.nameTemplate}`,
      )
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        "Failed to save the ad naming convention"
      toast.error(message)
    }
  }

  if (isLoadingDefaults || isLoadingCategories) {
    return (
      <div className="space-y-2">
        <h3>Ad naming &amp; tags</h3>
        <p className="text-sm text-system-gray-400">Loading…</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3>Ad naming &amp; tags</h3>
        <p className="max-w-3xl text-sm text-system-gray-400">
          Click tag categories into the order they appear in an ad name. AdForge
          composes new names from this, requires a tag for every position before
          a draft can be saved or blasted, and can read the tags back out of an
          existing ad name (test it below).
        </p>
      </div>

      <div>
        <div className="max-w-3xl space-y-2">
          <span className="text-xs font-medium text-system-gray-400">
            Name template
          </span>
          <div className="flex min-h-[3.25rem] flex-wrap items-center gap-1 rounded-md border border-dashed border-system-gray-700 bg-system-gray-950 px-3 py-2">
            {positions.length === 0 ? (
              <span className="text-sm text-system-gray-500">
                Click a category below to add the first position.
              </span>
            ) : (
              positions.map((row, index) => (
                <div key={row.uid} className="flex items-center gap-1">
                  {index > 0 && (
                    <span className="px-0.5 font-mono text-xs text-system-gray-500">
                      {separator === " " ? "␣" : separator}
                    </span>
                  )}
                  <TemplateChip
                    position={index + 1}
                    label={labelFor(row.slug)}
                    isFirst={index === 0}
                    isLast={index === positions.length - 1}
                    onMoveLeft={() => move(index, -1)}
                    onMoveRight={() => move(index, 1)}
                    onRemove={() =>
                      setPositions(prev => prev.filter(r => r.uid !== row.uid))
                    }
                  />
                </div>
              ))
            )}
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label
                htmlFor="ad-naming-separator"
                className="text-xs font-medium text-system-gray-400"
              >
                Separator
              </label>
              <div className="flex items-center gap-2">
                {/* `width` (not a w-* class) — Input sizes itself inline. */}
                <Input
                  id="ad-naming-separator"
                  width={96}
                  className="font-mono"
                  value={separator}
                  onChange={e => setSeparator(e.target.value)}
                  placeholder="_"
                />
                <div className="flex items-center gap-1">
                  {SEPARATOR_PRESETS.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setSeparator(preset)}
                      title={`Use ${describeSeparator(preset)}`}
                      className={cn(
                        "rounded border px-2 py-1 font-mono text-xs",
                        separator === preset
                          ? "border-brand bg-brand/10 text-white"
                          : "border-system-gray-700 text-system-gray-300 hover:border-brand",
                      )}
                    >
                      {preset === " " ? "␣" : preset}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <span className="text-xs font-medium text-system-gray-400">
              Example name
            </span>
            <div className="min-h-[2.5rem] break-all rounded-md border border-system-gray-700 bg-system-gray-950 px-3 py-2 font-mono text-sm text-white">
              {preview || (
                <span className="text-system-gray-600">
                  Add positions to see an example…
                </span>
              )}
            </div>
            {slugs.length > 0 && (
              <p className="text-xs text-system-gray-500">
                Saved as{" "}
                <code className="text-system-gray-300">
                  {slugs.map(s => `{${s}}`).join(separator)}
                </code>
                {" — "}
                {slugs.length} required tag{slugs.length === 1 ? "" : "s"} per
                ad
              </p>
            )}
            {!separator && (
              <p className="text-xs text-system-red-400">
                A separator is required — without one the values run together
                (&quot;FemaleGreat Talent&quot;) and the name can&apos;t be read
                back into tags.
              </p>
            )}
          </div>

          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isSaving}
            disabled={!separator}
            onClick={() => void handleSave()}
          >
            Save naming convention
          </Button>
        </div>

        {/* ---- Category pool + inline management ---- */}
        <div className="max-w-3xl space-y-2 border-t border-system-gray-800 pt-4">
          <span className="text-xs font-medium text-system-gray-400">
            Tag categories
          </span>

          {poolCategories.length === 0 && categories.length > 0 ? (
            <p className="text-xs text-system-gray-500">
              Every category is in the template.
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {poolCategories.map(category => (
                <PoolChip
                  key={category.id}
                  label={formatTagLabel(category.label)}
                  optionCount={category.options.length}
                  onAdd={() => addPosition(category.slug)}
                />
              ))}
            </div>
          )}

          {/* Values + rename, using the SAME row component as the paid-media
              optimization page's tag panel — one place defines how a category
              and its values look and how a value is added. Selection is
              read-only here: this manages the taxonomy, it doesn't tag an item.
              `onRename` is omitted for system categories so their canonical
              labels stay put. */}
          {categories.length > 0 && (
            <div className="rounded-md border border-system-blue-600/20 bg-system-blue-900/10 px-3">
              {categories.map(category => (
                <TagCategoryRow
                  key={category.id}
                  category={category}
                  readOnlySelection
                  selectedOptionIds={NO_SELECTION}
                  onChange={noop}
                  onCreateOption={label => createOption(category.slug, label)}
                  onRename={
                    category.isSystem
                      ? undefined
                      : label => renameCategory(category.slug, label)
                  }
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <Input
              width={224}
              value={newCategoryLabel}
              onChange={e => setNewCategoryLabel(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") void handleCreateCategory()
              }}
              placeholder="New category, e.g. Talent"
            />
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-system-gray-400">
              <Checkbox
                checked={newCategoryIsFreeText}
                onChange={e => setNewCategoryIsFreeText(e.target.checked)}
              />
              Free text
            </label>
            <Button
              variant={{ variant: "solid", color: "white" }}
              size="sm"
              type="button"
              isLoading={isCreating}
              disabled={!newCategoryLabel.trim()}
              onClick={() => void handleCreateCategory()}
            >
              Add category
            </Button>
            <span className="text-xs text-system-gray-500">
              Values are added as editors tag ads.
            </span>
          </div>
        </div>
      </div>

      <AdNameTester
        hasSavedConvention={!!defaults?.adNaming?.positions?.length}
      />
    </div>
  )
}
